import { useState, useMemo, useEffect } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Network, Globe, MapPin, Building2, Trash2, PowerOff, Power } from 'lucide-react';
import { toast } from 'sonner';
import { useCrud } from '../../api/hooks/useCrud';
import { api } from '../../api/client';
import { ROUTES } from '../../api/routes';
import { DataTable } from '../../components/ui/DataTable';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal, PasswordConfirmModal } from '../../components/ui';
import { useDevStore } from '../../store/useDevStore';
import '../../components/ui/datatable.css';

interface Rede {
  id: string;
  nome: string;
  descricao?: string;
  tipo?: string;
  ativo: boolean;
  _count?: { estados: number };
  _deepCount?: { municipios: number; escolas: number };
  criadoEm: string;
}

export interface HierarquiaNode {
  numero: number;
  label: string;
  icone: string;
}

export interface TipoRedePreset {
  id: string;
  label: string;
  icone: string;
  badge: string;
  escopo: string;
  exemploNome: string;
  premissaPadrao: string;
  hierarquia: HierarquiaNode[];
}

export const TIPOS_REDE_PRESETS: TipoRedePreset[] = [
  {
    id: 'nacional',
    label: 'Rede Nacional',
    icone: '🌐',
    badge: 'Federal',
    escopo: 'Institutos & Escolas Federais',
    exemploNome: 'Rede Nacional',
    premissaPadrao: 'Supervisão de unidades federais e polos tecnológicos em território nacional.',
    hierarquia: [
      { numero: 1, label: 'Rede Nacional', icone: '🌐' },
      { numero: 2, label: 'Sedes Estaduais', icone: '🏢' },
      { numero: 3, label: 'Polos Municipais', icone: '🏛️' },
      { numero: 4, label: 'Institutos Federais', icone: '🎓' },
    ],
  },
  {
    id: 'estadual',
    label: 'Rede Estadual',
    icone: '🏢',
    badge: 'Governos',
    escopo: 'Fundamental II & Médio',
    exemploNome: 'Rede Estadual',
    premissaPadrao: 'Coordenação macroeducacional da rede estadual com articulação nos estados.',
    hierarquia: [
      { numero: 1, label: 'Rede Mantenedora', icone: '🌐' },
      { numero: 2, label: 'Estados (SEDUC)', icone: '🏢' },
      { numero: 3, label: 'Regionais / Polos', icone: '🏛️' },
      { numero: 4, label: 'Escolas Estaduais', icone: '🎒' },
    ],
  },
  {
    id: 'municipal',
    label: 'Rede Municipal',
    icone: '🏫',
    badge: 'Prefeituras',
    escopo: 'Educação Básica & Creches',
    exemploNome: 'Rede Municipal',
    premissaPadrao: 'Gestão de redes municipais de ensino, creches e transporte escolar nos estados.',
    hierarquia: [
      { numero: 1, label: 'Rede Mantenedora', icone: '🌐' },
      { numero: 2, label: 'Estados / UFs', icone: '🏢' },
      { numero: 3, label: 'Municípios (SME)', icone: '🏛️' },
      { numero: 4, label: 'Escolas & Creches', icone: '🎒' },
    ],
  },
  {
    id: 'superior',
    label: 'Rede Universitária',
    icone: '🎓',
    badge: 'Superior',
    escopo: 'Ensino Superior & EAD',
    exemploNome: 'Rede Universitária',
    premissaPadrao: 'Gestão acadêmica de frequência e polos universitários em nível nacional.',
    hierarquia: [
      { numero: 1, label: 'Reitoria Geral', icone: '🌐' },
      { numero: 2, label: 'Coordenações UF', icone: '🏢' },
      { numero: 3, label: 'Polos Municipais', icone: '🏛️' },
      { numero: 4, label: 'Faculdades & Campi', icone: '🎓' },
    ],
  },
  {
    id: 'privada',
    label: 'Rede Privada',
    icone: '💼',
    badge: 'Privado',
    escopo: 'Grupos & Colégios Privados',
    exemploNome: 'Rede Privada',
    premissaPadrao: 'Gestão unificada de unidades e colégios particulares em múltiplos estados.',
    hierarquia: [
      { numero: 1, label: 'Grupo Mantenedor', icone: '🌐' },
      { numero: 2, label: 'Regionais UF', icone: '🏢' },
      { numero: 3, label: 'Unidades Locais', icone: '🏛️' },
      { numero: 4, label: 'Colégios Privados', icone: '🎒' },
    ],
  },
  {
    id: 'customizada',
    label: 'Rede Customizada',
    icone: '✨',
    badge: 'Personalizado',
    escopo: 'Estrutura Livre & Especial',
    exemploNome: 'Rede Customizada',
    premissaPadrao: 'Estrutura institucional customizada conforme o modelo organizacional da mantenedora.',
    hierarquia: [
      { numero: 1, label: 'Mantenedora', icone: '🌐' },
      { numero: 2, label: 'Gestão Regional', icone: '🏢' },
      { numero: 3, label: 'Polos Administrativos', icone: '🏛️' },
      { numero: 4, label: 'Unidades de Ensino', icone: '🎒' },
    ],
  },
];

export function RedesPage() {
  const { data, isLoading, create, update, deactivate, refetch } = useCrud<Rede>('admin/redes');
  const devSettings = useDevStore((s) => s.settings);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Rede | null>(null);
  const [tipoSelecionado, setTipoSelecionado] = useState<string>('nacional');
  const [form, setForm] = useState({ nome: '', descricao: '' });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Rede | null>(null);
  const [redeToHardDelete, setRedeToHardDelete] = useState<Rede | null>(null);

  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // Entidades globais para totalizadores consolidados do topo
  const [allEstadosGlobal, setAllEstadosGlobal] = useState<any[]>([]);
  const [allMunicipiosGlobal, setAllMunicipiosGlobal] = useState<any[]>([]);
  const [allEscolasGlobal, setAllEscolasGlobal] = useState<any[]>([]);

  useEffect(() => {
    api.get(ROUTES.admin.estados).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data?.data ?? [];
      setAllEstadosGlobal(list);
    }).catch(() => {});

    api.get(ROUTES.admin.municipios).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data?.data ?? [];
      setAllMunicipiosGlobal(list);
    }).catch(() => {});

    api.get(ROUTES.escolas.listar).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data?.data ?? [];
      setAllEscolasGlobal(list);
    }).catch(() => {});
  }, []);

  const openNew = () => {
    setEditing(null);
    const presetPadrao = TIPOS_REDE_PRESETS[0];
    setTipoSelecionado(presetPadrao.id);
    setForm({
      nome: presetPadrao.exemploNome,
      descricao: presetPadrao.premissaPadrao,
    });
    setModalOpen(true);
  };
  const openEdit = (r: Rede) => {
    setEditing(r);
    setForm({ nome: r.nome, descricao: r.descricao || '' });
    setModalOpen(true);
  };

  const hasFormData = () => {
    if (editing) return false;
    return !!(form.nome.trim() || form.descricao.trim());
  };

  const handleCancelClick = () => {
    if (hasFormData()) {
      setDiscardConfirmOpen(true);
    } else {
      toast.dismiss();
      setModalOpen(false);
    }
  };

  const handleConfirmDiscard = () => {
    setDiscardConfirmOpen(false);
    toast.dismiss();
    setModalOpen(false);
  };

  const handleSave = async () => {
    if (!form.nome.trim()) { toast.warning('Nome da rede é obrigatório'); return; }
    if (!form.descricao.trim()) { toast.warning('Descrição Institucional é obrigatória'); return; }
    
    const normalize = (s: string) =>
      (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    const nomeNorm = normalize(form.nome);

    const redesExistentes = (data as Rede[]) || [];
    const duplicada = redesExistentes.find(
      (r) => (!editing || r.id !== editing.id) && normalize(r.nome) === nomeNorm,
    );
    if (duplicada) {
      toast.warning(`Já existe a rede de ensino "${duplicada.nome}" cadastrada na plataforma.`);
      return;
    }

    setSaving(true);
    try {
      if (editing) { await update.mutateAsync({ id: editing.id, ...form } as any); toast.success('Rede atualizada!'); }
      else { await create.mutateAsync(form as any); toast.success('Rede criada!'); }
      setModalOpen(false);
    } catch (e: any) {
      toast.warning(e?.response?.data?.message || 'Não foi possível salvar a rede');
    } finally { setSaving(false); }
  };

  const handleActivateRede = async (r: Rede) => {
    try {
      await update.mutateAsync({ id: r.id, ativo: true } as any);
      toast.success('Rede ativada com sucesso!');
      refetch();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao ativar rede');
    }
  };

  const handleHardDeleteRede = (r: Rede) => {
    setRedeToHardDelete(r);
  };

  const confirmHardDeleteRede = async () => {
    if (!redeToHardDelete) return;
    const r = redeToHardDelete;
    setRedeToHardDelete(null);
    try {
      await api.delete(`/admin/redes/hard/${r.id}`);
      toast.success('Rede removida com sucesso!');
      refetch();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao remover rede permanentemente');
    }
  };

  const requestDelete = (r: Rede) => {
    const estados = r._count?.estados || 0;
    const municipios = r._deepCount?.municipios || 0;
    const escolas = r._deepCount?.escolas || 0;
    if (estados > 0 || municipios > 0 || escolas > 0) {
      const detalhes: string[] = [];
      if (estados > 0) detalhes.push(`${estados} estado${estados !== 1 ? 's' : ''}`);
      if (municipios > 0) detalhes.push(`${municipios} município${municipios !== 1 ? 's' : ''}`);
      if (escolas > 0) detalhes.push(`${escolas} escola${escolas !== 1 ? 's' : ''}`);
      toast.warning(`Não é possível desativar a rede "${r.nome}". Ela possui ${detalhes.join(', ')} vinculados.`);
      return;
    }
    setDeleteConfirm(r);
  };
  const confirmDeleteRede = async () => {
    if (!deleteConfirm) return;
    const id = deleteConfirm.id;
    setDeleteConfirm(null);
    try {
      await deactivate.mutateAsync(id);
      toast.success('Rede desativada com sucesso');
      refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.error?.message || e?.response?.data?.message || 'Não foi possível desativar a rede';
      toast.warning(msg);
    }
  };

  const [search, setSearch] = useState('');

  const filteredData = useMemo(() => {
    const all = data || [];
    const filtered = !search.trim()
      ? all
      : all.filter((r: Rede) =>
          r.nome.toLowerCase().includes(search.toLowerCase()) ||
          (r.descricao && r.descricao.toLowerCase().includes(search.toLowerCase()))
        );

    return [...filtered].sort((a: Rede, b: Rede) => {
      if (a.ativo !== b.ativo) return a.ativo ? -1 : 1;
      return a.nome.localeCompare(b.nome);
    });
  }, [data, search]);

  /* ── KPIs Consolidados (Topo da página com UFs únicas) ── */
  const stats = useMemo(() => {
    const all = (data as Rede[]) || [];
    const ativas = all.filter(r => r.ativo);
    const inativas = all.filter(r => !r.ativo);

    // 1. Estados únicos ativos e inativos (Total Brasil: sempre 27 UFs únicas)
    const estadosAtivosSiglas = new Set<string>();
    (allEstadosGlobal || []).forEach((e: any) => {
      const siglaNorm = (e.sigla || e.nome || '').trim().toUpperCase();
      if (siglaNorm && e.ativo) {
        estadosAtivosSiglas.add(siglaNorm);
      }
    });
    const estadosAtivos = estadosAtivosSiglas.size || (allEstadosGlobal.length > 0 ? allEstadosGlobal.filter(e => e.ativo).length : 27);
    const estadosInativos = (allEstadosGlobal || []).filter((e: any) => !e.ativo).length;

    // 2. Municípios únicos ativos, em operação e inativos
    const munAtivosSet = new Set<string>();
    const munEmOperacaoSet = new Set<string>();
    (allMunicipiosGlobal || []).forEach((m: any) => {
      if (m.ativo) {
        const estadoSigla = (m.estado?.sigla || m.estado?.nome || m.estadoId || '').trim().toUpperCase();
        const nomeNorm = (m.nome || '').trim().toUpperCase();
        const chave = `${nomeNorm}__${estadoSigla}`;
        munAtivosSet.add(chave);
        if ((m._count?.escolas || 0) > 0) {
          munEmOperacaoSet.add(chave);
        }
      }
    });
    const municipiosAtivos = munAtivosSet.size;
    const municipiosEmOperacao = munEmOperacaoSet.size;
    const municipiosPendentes = Math.max(0, municipiosAtivos - municipiosEmOperacao);
    const municipiosInativos = (allMunicipiosGlobal || []).filter((m: any) => !m.ativo).length;

    // 3. Escolas ativas, em atividade, em implantação e inativas
    const ativasEsc = (allEscolasGlobal || []).filter((esc: any) => esc.ativo);
    const escolasAtivas = ativasEsc.length;
    const escolasInativas = (allEscolasGlobal || []).filter((esc: any) => !esc.ativo).length;
    const escolasEmAtividade = ativasEsc.filter(
      (e: any) =>
        e.wizardConcluido ||
        (Number(e.wizardEtapaAtual || 0) >= 7 &&
          (e._count?.turmas || 0) > 0 &&
          (e._count?.alunos || 0) > 0),
    ).length;
    const escolasEmImplantacao = Math.max(0, escolasAtivas - escolasEmAtividade);

    return {
      redesAtivas: ativas.length,
      redesInativas: inativas.length,
      estadosAtivos,
      estadosInativos,
      municipiosAtivos,
      municipiosEmOperacao,
      municipiosPendentes,
      municipiosInativos,
      escolasAtivas,
      escolasEmAtividade,
      escolasEmImplantacao,
      escolasInativas,
    };
  }, [data, allEstadosGlobal, allMunicipiosGlobal, allEscolasGlobal]);

  const columns: ColumnDef<Rede>[] = [
    { accessorKey: 'nome', header: 'Nome da Rede', cell: ({ row }) => {
      const isAtivo = row.original.ativo;
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          opacity: isAtivo ? 1 : 0.45,
          transition: 'opacity 0.2s ease',
        }}>
          <div style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: isAtivo ? 'rgba(139, 92, 246, 0.12)' : 'rgba(156, 163, 175, 0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Network size={16} style={{ color: isAtivo ? '#8b5cf6' : '#9ca3af' }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, color: isAtivo ? 'inherit' : '#64748b' }}>{row.original.nome}</div>
            {row.original.descricao && (
              <div style={{ fontSize: '.75rem', color: '#9ca3af', marginTop: 1 }}>{row.original.descricao}</div>
            )}
          </div>
        </div>
      );
    }},
    {
      id: 'estados',
      header: 'Estados',
      cell: ({ row }) => {
        const qtd = row.original._count?.estados || 0;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: row.original.ativo ? 1 : 0.5 }}>
            <Globe size={14} style={{ color: '#6366f1' }} />
            <span style={{ fontWeight: 600, fontSize: '.85rem' }}>
              {qtd} {qtd === 1 ? 'estado' : 'estados'}
            </span>
          </div>
        );
      }
    },
    {
      id: 'municipios',
      header: 'Municípios',
      cell: ({ row }) => {
        const qtd = row.original._deepCount?.municipios || 0;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: row.original.ativo ? 1 : 0.5 }}>
            <MapPin size={14} style={{ color: '#f59e0b' }} />
            <span style={{ fontWeight: 600, fontSize: '.85rem' }}>
              {qtd} {qtd === 1 ? 'município' : 'municípios'}
            </span>
          </div>
        );
      }
    },
    {
      id: 'escolas',
      header: 'Escolas',
      cell: ({ row }) => {
        const qtd = row.original._deepCount?.escolas || 0;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: row.original.ativo ? 1 : 0.5 }}>
            <Building2 size={14} style={{ color: '#10b981' }} />
            <span style={{ fontWeight: 600, fontSize: '.85rem' }}>
              {qtd} {qtd === 1 ? 'escola' : 'escolas'}
            </span>
          </div>
        );
      }
    },
    { accessorKey: 'ativo', header: 'Status', cell: ({ row }) => {
      if (!row.original.ativo) {
        return <span className="badge badge-inactive">Inativo</span>;
      }
      const temEstados = (row.original._count?.estados || 0) > 0;
      if (temEstados) {
        return <span className="badge badge-active" title="Rede ativa com estados vinculados">Ativo</span>;
      }
      return (
        <span
          className="badge"
          style={{ background: 'rgba(245,158,11,0.15)', color: '#d97706', border: '1px solid rgba(245,158,11,0.35)', fontWeight: 700 }}
          title="Rede cadastrada, mas sem estados vinculados (Pendente de abrangência)"
        >
          Pendente
        </span>
      );
    }},
    { id: 'actions', header: '', cell: ({ row }) => (
      <div style={{ display: 'flex', gap: 4 }}>
        <button className="action-btn" onClick={() => openEdit(row.original)} title="Editar"><Pencil size={15} /></button>
        {devSettings.allowDeleteRedes && (
          row.original.ativo ? (
            <button className="action-btn action-btn-danger" onClick={() => requestDelete(row.original)} title="Desativar"><PowerOff size={15} /></button>
          ) : (
            <>
              <button className="action-btn" onClick={() => handleActivateRede(row.original)}
                title="Reativar" style={{ color: '#10b981', border: '1px solid #10b981', background: 'rgba(16,185,129,0.1)' }}><Power size={15} /></button>
              {devSettings.allowHardDelete && (
                <button className="action-btn action-btn-danger" onClick={() => handleHardDeleteRede(row.original)}
                  title="Remover permanentemente"><Trash2 size={15} /></button>
              )}
            </>
          )
        )}
      </div>
    )},
  ];

  const kpis = [
    {
      icon: Network,
      label: 'Redes de Ensino',
      value: stats.redesAtivas,
      subValue: stats.redesInativas > 0
        ? `${stats.redesAtivas} ativas • ${stats.redesInativas} inativa${stats.redesInativas !== 1 ? 's' : ''}`
        : undefined,
      color: '#8b5cf6'
    },
    {
      icon: Globe,
      label: 'Estados',
      value: stats.estadosAtivos,
      subValue: stats.estadosInativos > 0
        ? `${stats.estadosAtivos} ativos • ${stats.estadosInativos} inativo${stats.estadosInativos !== 1 ? 's' : ''}`
        : undefined,
      color: '#6366f1'
    },
    {
      icon: MapPin,
      label: 'Municípios',
      value: stats.municipiosAtivos,
      subValue: (stats.municipiosPendentes > 0 || stats.municipiosInativos > 0)
        ? `${stats.municipiosEmOperacao} em operação • ${stats.municipiosPendentes} pendente${stats.municipiosPendentes !== 1 ? 's' : ''}${stats.municipiosInativos > 0 ? ` • ${stats.municipiosInativos} inativo${stats.municipiosInativos !== 1 ? 's' : ''}` : ''}`
        : `${stats.municipiosAtivos} ativos`,
      color: '#f59e0b'
    },
    {
      icon: Building2,
      label: 'Escolas',
      value: stats.escolasAtivas,
      subValue: (stats.escolasEmImplantacao > 0 || stats.escolasInativas > 0)
        ? `${stats.escolasEmAtividade} em atividade • ${stats.escolasEmImplantacao} em implantação${stats.escolasInativas > 0 ? ` • ${stats.escolasInativas} inativa${stats.escolasInativas !== 1 ? 's' : ''}` : ''}`
        : `${stats.escolasAtivas} ativas`,
      color: '#10b981'
    },
  ];

  return (
    <div className="admin-page animate-fadeIn">

      {/* ═══ HERO BANNER ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #3b1f7e 0%, #6d28d9 40%, #8b5cf6 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(139,92,246,.15)' }} />
        <div style={{ position: 'absolute', bottom: -40, left: '30%', width: 160, height: 160, borderRadius: '50%', background: 'rgba(109,40,217,.1)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <Network size={18} style={{ color: '#c4b5fd' }} />
                <span style={{ color: 'rgba(255,255,255,.4)', fontSize: '.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.12em' }}>Gestão</span>
              </div>
              <h1 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>Redes de Ensino</h1>
              <p style={{ color: 'rgba(255,255,255,.45)', fontSize: '.82rem', marginTop: 4 }}>
                {stats.redesAtivas} rede{stats.redesAtivas !== 1 ? 's' : ''} ativa{stats.redesAtivas !== 1 ? 's' : ''} {stats.redesInativas > 0 ? `(${stats.redesInativas} inativa${stats.redesInativas !== 1 ? 's' : ''})` : ''} cadastrada{stats.redesAtivas !== 1 ? 's' : ''} na plataforma
              </p>
            </div>
            <button onClick={openNew} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10,
              background: '#ffffff', color: '#5b21b6', fontWeight: 700, fontSize: '.85rem', border: 'none', cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(0,0,0,0.15)', transition: 'all 0.15s ease',
            }}>
              <Plus size={16} /> Nova Rede
            </button>
          </div>
        </div>
      </div>

      {/* ═══ KPIS ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
        {kpis.map((k, i) => (
          <div key={i} style={{
            background: 'var(--bg-card, white)', borderRadius: 14, padding: '18px 20px',
            border: '1px solid var(--border, #e5e7eb)', display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10, background: `${k.color}15`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <k.icon size={22} style={{ color: k.color }} />
            </div>
            <div>
              <div style={{ fontSize: '.75rem', color: '#6b7280', fontWeight: 500 }}>{k.label}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-main, #111827)', lineHeight: 1.2 }}>{k.value}</span>
                {k.subValue && (
                  <span style={{ fontSize: '.72rem', color: '#9ca3af', fontWeight: 600 }}>
                    ({k.subValue})
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ═══ TABELA ═══ */}
      <div style={{ borderRadius: 12, border: '1px solid var(--border, #e5e7eb)', overflow: 'hidden', marginBottom: 32 }}>
        <div style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px', background: '#111827', border: 'none',
        }}>
          <Network size={16} style={{ color: '#818cf8', flexShrink: 0 }} />
          <span style={{ flex: 1, fontWeight: 700, fontSize: '.9rem', color: 'rgba(255,255,255,.9)' }}>
            Redes de Ensino
          </span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar rede..."
            style={{
              width: 200, padding: '6px 12px', borderRadius: 8,
              border: '1px solid rgba(255,255,255,.15)', background: 'rgba(255,255,255,.08)',
              color: 'rgba(255,255,255,.8)', fontSize: '.8rem', outline: 'none',
            }}
          />
        </div>
        <div style={{ padding: '0 0 4px' }}>
          <DataTable columns={columns} data={filteredData} isLoading={isLoading} searchPlaceholder="" showToolbar={false} />
        </div>
      </div>

      {/* ═══ MODAL EXPANDIDO COM ONBOARDING EM 2 COLUNAS & HIERARQUIA VISUAL ═══ */}
      <Modal
        open={modalOpen}
        onClose={handleCancelClick}
        dialogId="modal-nova-rede"
        size="xl"
        title={editing ? 'Editar Rede Mantenedora' : 'Cadastrar Nova Rede'}
        footer={
          <>
            <button className="btn-cancel" onClick={editing ? () => setModalOpen(false) : handleCancelClick}>
              Cancelar
            </button>
            <button
              className="btn-save"
              onClick={handleSave}
              disabled={saving || !form.nome.trim() || !form.descricao.trim()}
              style={{
                opacity: (form.nome.trim() && form.descricao.trim()) ? 1 : 0.5,
                cursor: (form.nome.trim() && form.descricao.trim()) ? (saving ? 'wait' : 'pointer') : 'not-allowed',
                transition: 'all 0.2s ease',
              }}
              title={!form.nome.trim() || !form.descricao.trim() ? 'Preencha o nome e a descrição institucional da rede para liberar o salvamento' : undefined}
            >
              {saving ? 'Salvando...' : 'Salvar Rede'}
            </button>
          </>
        }
      >
        {editing ? (
          /* Modo Edição: Formulário Direto */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-group">
              <label className="form-label" htmlFor="rede-nome" style={{ fontWeight: 700 }}>
                Nome da Rede *
              </label>
              <input
                id="rede-nome"
                data-lab-field="nome"
                className="form-input"
                name="nome"
                value={form.nome}
                onChange={e => setForm({ ...form, nome: e.target.value })}
                placeholder="Ex: Rede Estadual, Rede Municipal"
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="rede-descricao" style={{ fontWeight: 700 }}>
                Descrição Institucional *
              </label>
              <textarea
                id="rede-descricao"
                data-lab-field="descricao"
                className="form-input"
                name="descricao"
                rows={3}
                value={form.descricao}
                onChange={e => setForm({ ...form, descricao: e.target.value })}
                placeholder="Descrição da finalidade e abrangência institucional..."
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>
        ) : (
          /* Modo Criação: Layout de 2 Colunas Limpas e Amplo Aproveitamento */
          <div style={{
            display: 'grid',
            gridTemplateColumns: '195px 1fr',
            gap: 18,
            alignItems: 'stretch',
          }}>
            {/* ── COLUNA ESQUERDA: Tipos de Rede (Ícones Grandes & Nomes Diretos) ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: '.72rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                REDE MANTENEDORA
              </span>

              {TIPOS_REDE_PRESETS.map((preset) => {
                const isSelected = tipoSelecionado === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    data-lab-type-preset={preset.id}
                    onClick={() => {
                      setTipoSelecionado(preset.id);
                      setForm({
                        nome: preset.exemploNome,
                        descricao: preset.premissaPadrao,
                      });
                    }}
                    style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: isSelected ? '1.5px solid #4f46e5' : '1px solid #e2e8f0',
                      background: isSelected ? 'rgba(99, 102, 241, 0.08)' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    <span style={{ fontSize: '1.5rem', flexShrink: 0, lineHeight: 1 }}>{preset.icone}</span>
                    <span style={{
                      fontWeight: 700,
                      fontSize: '.84rem',
                      color: isSelected ? '#3730a3' : '#1e293b',
                      whiteSpace: 'nowrap',
                    }}>
                      {preset.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* ── COLUNA DIREITA: Formulário & Diagrama de Hierarquia em Cascata ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, justifyContent: 'space-between' }}>
              {/* Campos do Formulário */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" htmlFor="rede-nome" style={{ fontWeight: 700, marginBottom: 4, fontSize: '.82rem' }}>
                    Nome da Rede *
                  </label>
                  <input
                    id="rede-nome"
                    data-lab-field="nome"
                    className="form-input"
                    name="nome"
                    value={form.nome}
                    onChange={e => setForm({ ...form, nome: e.target.value })}
                    placeholder="Ex: Rede Nacional, Rede Estadual, etc."
                    style={{ fontSize: '.84rem', padding: '7px 10px' }}
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" htmlFor="rede-descricao" style={{ fontWeight: 700, marginBottom: 4, fontSize: '.82rem' }}>
                    Descrição Institucional *
                  </label>
                  <textarea
                    id="rede-descricao"
                    data-lab-field="descricao"
                    className="form-input"
                    name="descricao"
                    rows={2}
                    value={form.descricao}
                    onChange={e => setForm({ ...form, descricao: e.target.value })}
                    placeholder="Finalidade e diretrizes institucionais..."
                    style={{ resize: 'vertical', fontSize: '.84rem', padding: '7px 10px' }}
                  />
                </div>
              </div>

              {/* ── ESTRUTURA CANÔNICA: Hierarquia em Cascata de Pastas ── */}
              {(() => {
                const activePreset = TIPOS_REDE_PRESETS.find(p => p.id === tipoSelecionado) || TIPOS_REDE_PRESETS[0];
                const nomeRedeExibicao = form.nome?.trim() || activePreset.exemploNome;
                return (
                  <div style={{
                    padding: '8px 12px',
                    borderRadius: 8,
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    opacity: 0.5,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '.72rem', fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        ESTRUTURA CANÔNICA
                      </span>
                      <span style={{ fontSize: '.68rem', color: '#64748b', fontWeight: 600 }}>
                        {activePreset.escopo}
                      </span>
                    </div>

                    {/* Árvore Hierárquica em Cascata (Estilo Pastas / Setas de Nível) */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {activePreset.hierarquia.map((no, idx) => {
                        const recuo = idx * 24;
                        const labelExibicao = idx === 0 ? nomeRedeExibicao : no.label;
                        const isRedeRoot = idx === 0;

                        return (
                          <div
                            key={idx}
                            style={{
                              marginLeft: recuo,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            {!isRedeRoot && (
                              <span style={{ color: '#6366f1', fontSize: '.85rem', fontWeight: 800, lineHeight: 1 }}>
                                ↳
                              </span>
                            )}
                            <div
                              style={{
                                padding: '3px 9px',
                                borderRadius: 6,
                                background: isRedeRoot ? 'rgba(99, 102, 241, 0.1)' : '#fff',
                                border: isRedeRoot ? '1.5px solid #6366f1' : '1px solid #e2e8f0',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 7,
                                boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                                maxWidth: '100%',
                              }}
                            >
                              <span style={{ fontSize: '.95rem', flexShrink: 0 }}>{no.icone}</span>
                              <span style={{
                                fontWeight: 700,
                                fontSize: '.72rem',
                                color: isRedeRoot ? '#3730a3' : '#1e293b',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                letterSpacing: '0.02em',
                              }}>
                                {labelExibicao.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </Modal>

      {/* Diálogo Institucional de Confirmação para Descarte de Dados */}
      <ConfirmModal
        open={discardConfirmOpen}
        title="Descartar alterações?"
        message="Tem certeza que deseja cancelar o cadastro da rede? Todas as informações preenchidas serão perdidas."
        confirmLabel="Sim, descartar"
        cancelLabel="Continuar preenchendo"
        variant="warning"
        onCancel={() => setDiscardConfirmOpen(false)}
        onConfirm={handleConfirmDiscard}
      />

      <ConfirmModal
        open={!!deleteConfirm}
        title="Desativar rede"
        message={deleteConfirm ? `Desativar a rede "${deleteConfirm.nome}"?` : ''}
        confirmLabel="Desativar"
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => void confirmDeleteRede()}
      />

      {/* Modal de Confirmação com Senha de Segurança para Remoção Permanente de Rede */}
      <PasswordConfirmModal
        open={!!redeToHardDelete}
        title="Remover Rede Permanentemente"
        description={`Para remover permanentemente a rede "${redeToHardDelete?.nome}", digite a palavra-chave de autorização:`}
        requiredPassword="marcelo"
        confirmLabel="Remover Rede"
        onCancel={() => setRedeToHardDelete(null)}
        onConfirm={confirmHardDeleteRede}
      />
    </div>
  );
}
